import { createScopedErrors, registerCodes, reportInvalidInput } from '@norkron/web-errors';
import { forwardRef, useCallback, createContext, useContext, useState, useMemo, useRef, Fragment, useEffect } from 'react';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import { EmptyState } from '@norkron/web-states';
import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { z } from 'zod';
import { useThemeVar } from '@norkron/web-themer/react';
import { RichTooltip } from '@norkron/web-tooltip';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useFocusRestore } from '@norkron/web-a11y';
import { registerLazy } from '@norkron/web-conformance/register-lazy';

// src/error-codes.ts
var WEB_DND_LIB = "@norkron/web-dnd";
var WEB_DND_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-dnd");
var { warn, panic } = createScopedErrors(WEB_DND_LIB, WEB_DND_TOKEN);
try {
  registerCodes(WEB_DND_LIB, {
    WEB_DND_INVALID_INPUT: {
      description: "Runtime input validation rejected the value passed to a public web-dnd export (Wave 10 zod mandate). Payload follows WEB_INVALID_INPUT_SHAPE: { fieldPath, expected, received }.",
      severity: "warning",
      docsAnchor: "web-dnd/invalid-input",
      exampleFix: "Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib."
    },
    WEB_DND_REORDER_OUT_OF_RANGE: {
      description: "A reorder operation targeted an index outside the list range. The hook clamps to [0, length-1] and emits this warning so the caller can correct the source of the bad index.",
      severity: "warning",
      docsAnchor: "web-dnd/reorder-out-of-range",
      exampleFix: "Clamp the requested fromIndex / toIndex against items.length before calling move(); use KeyboardDragHandle from this lib instead of dispatching reorders manually."
    },
    WEB_DND_DROP_REJECTED: {
      description: "A drop event arrived on a target whose `accept` list did not include the dragged itemType. The drop is ignored and this warning surfaces so consumers can audit mismatched item types.",
      severity: "warning",
      docsAnchor: "web-dnd/drop-rejected",
      exampleFix: "Either widen the receiving target's accept list to include the dragged itemType, or set the source's itemType to match what the target accepts."
    },
    DND_NO_KEYBOARD_FALLBACK: {
      description: "selfCheck found a drag-and-drop region with no keyboard-equivalent affordance. Pointer-only interaction excludes keyboard users.",
      severity: "warning",
      docsAnchor: "web-dnd/self-check#no-keyboard-fallback",
      exampleFix: "Render <KeyboardDragHandle /> alongside the pointer-drag surface."
    },
    DND_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck exceeded its per-call time budget while walking dnd instances; partial results returned.",
      severity: "info",
      docsAnchor: "web-dnd/self-check#budget-exceeded",
      exampleFix: "Increase budgetMs or reduce the number of dnd regions per page."
    },
    DND_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck threw internally while inspecting a dnd instance. Exception caught + reported as a finding.",
      severity: "info",
      docsAnchor: "web-dnd/self-check#internal-error",
      exampleFix: "Inspect finding.message + finding.extras for the underlying error."
    }
  });
} catch {
}

// src/version.ts
var LIB_VERSION = "0.2.4";

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var DraggableListPropsSchema = z.object({
  items: z.array(z.unknown()),
  getId: z.function(),
  renderItem: z.function(),
  onReorder: z.function(),
  ariaLabel: z.string().optional(),
  className: z.string().optional(),
  itemTypeFor: z.function().optional()
});
var DraggableItemPropsSchema = z.object({
  id: z.string().min(1),
  index: z.number().int().nonnegative(),
  children: z.unknown().optional(),
  className: z.string().optional(),
  itemType: z.string().optional()
});
var UseDragOptionsSchema = z.object({
  id: z.string().min(1),
  itemType: z.string().optional(),
  disabled: z.boolean().optional(),
  onDragStart: z.function().optional(),
  onDragEnd: z.function().optional()
});
var UseDropOptionsSchema = z.object({
  accept: z.array(z.string().min(1)).optional(),
  disabled: z.boolean().optional(),
  onDrop: z.function().optional(),
  onDragEnter: z.function().optional(),
  onDragLeave: z.function().optional()
});
var UseDraggableListOptionsSchema = z.object({
  items: z.array(z.unknown()),
  getId: z.function(),
  onReorder: z.function(),
  itemTypeFor: z.function().optional()
});
var KeyboardDragHandlePropsSchema = z.object({
  index: z.number().int().nonnegative(),
  listSize: z.number().int().positive(),
  label: z.string().min(1),
  disabled: z.boolean().optional(),
  className: z.string().optional()
});

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: WEB_DND_LIB,
      libToken: WEB_DND_TOKEN,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
    return input;
  }
  return parsed.data;
}
function useDraggableList(options) {
  const validated = validateInput(
    UseDraggableListOptionsSchema,
    "useDraggableList",
    options
  );
  const { items, getId, onReorder } = validated;
  const [grabbedIndex, setGrabbedIndex] = useState(null);
  const originRef = useRef(null);
  const grabbedId = useMemo(() => {
    if (grabbedIndex == null) return null;
    const it = items[grabbedIndex];
    return it === void 0 ? null : getId(it);
  }, [grabbedIndex, items, getId]);
  const clamp = useCallback(
    (idx) => Math.max(0, Math.min(items.length - 1, idx)),
    [items.length]
  );
  const grab = useCallback(
    (index) => {
      if (index < 0 || index >= items.length) {
        warn({
          fn: "useDraggableList.grab",
          code: "WEB_DND_REORDER_OUT_OF_RANGE",
          message: "grab index outside list range",
          received: { index, length: items.length },
          expected: `index in [0, ${items.length - 1}]`
        });
        return;
      }
      originRef.current = index;
      setGrabbedIndex(index);
    },
    [items.length]
  );
  const cancel = useCallback(() => {
    setGrabbedIndex(null);
    originRef.current = null;
  }, []);
  const moveIndex = useCallback(
    (from, to) => {
      if (from < 0 || from >= items.length) {
        warn({
          fn: "useDraggableList.moveIndex",
          code: "WEB_DND_REORDER_OUT_OF_RANGE",
          message: "fromIndex outside list range",
          received: { from, length: items.length },
          expected: `fromIndex in [0, ${items.length - 1}]`
        });
        return;
      }
      const target = clamp(to);
      if (from === target) return;
      const next = items.slice();
      const [moved] = next.splice(from, 1);
      if (moved === void 0) return;
      next.splice(target, 0, moved);
      onReorder(next);
    },
    [items, clamp, onReorder]
  );
  const moveBy = useCallback(
    (delta) => {
      if (grabbedIndex == null) return;
      const next = clamp(grabbedIndex + delta);
      if (next === grabbedIndex) return;
      moveIndex(grabbedIndex, next);
      setGrabbedIndex(next);
    },
    [grabbedIndex, clamp, moveIndex]
  );
  const dropAt = useCallback(
    (index) => {
      if (grabbedIndex == null) return;
      const target = clamp(index);
      if (target !== grabbedIndex) moveIndex(grabbedIndex, target);
      setGrabbedIndex(null);
      originRef.current = null;
    },
    [grabbedIndex, clamp, moveIndex]
  );
  return useMemo(
    () => ({
      items,
      grabbedId,
      grabbedIndex,
      originIndex: originRef.current,
      grab,
      cancel,
      dropAt,
      moveBy,
      moveIndex
    }),
    [items, grabbedId, grabbedIndex, grab, cancel, dropAt, moveBy, moveIndex]
  );
}
var KeyboardDragHandle = forwardRef(
  function KeyboardDragHandle2(props, ref) {
    const validated = validateInput(
      KeyboardDragHandlePropsSchema,
      "KeyboardDragHandle",
      props
    );
    const { index, listSize, label, disabled, className } = validated;
    const ctx = useDraggableListContext();
    const grabbed = ctx?.grabbedIndex === index;
    const tone = useThemeVar("color-fg-default");
    const onKey = useCallback(
      (e) => {
        if (disabled || !ctx) return;
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          if (grabbed) ctx.dropAt(ctx.grabbedIndex ?? index);
          else ctx.grab(index);
          return;
        }
        if (!grabbed) return;
        if (e.key === "ArrowDown") {
          e.preventDefault();
          ctx.moveBy(1);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          ctx.moveBy(-1);
        } else if (e.key === "Escape") {
          e.preventDefault();
          ctx.cancelToOrigin();
        }
      },
      [disabled, ctx, grabbed, index]
    );
    const cls = [
      "dnd__handle",
      grabbed ? "dnd__handle--grabbed" : null,
      disabled ? "dnd__handle--disabled" : null,
      className
    ].filter(Boolean).join(" ");
    const description = grabbed ? `${label}. Currently grabbed at position ${(ctx?.grabbedIndex ?? index) + 1} of ${listSize}. Use arrow up and arrow down to move, Space or Enter to drop, Escape to cancel.` : `${label}. Press Space or Enter to grab, then use arrow up and arrow down to move.`;
    const button = /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        ref,
        className: cls,
        "aria-pressed": grabbed,
        "aria-disabled": disabled || void 0,
        "aria-roledescription": "draggable item",
        "aria-label": label,
        "data-dnd-handle": "true",
        "data-keyboard-drag-handle": "true",
        "data-dnd-index": index,
        onKeyDown: onKey,
        disabled,
        style: { color: tone },
        children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "dnd__handle-icon", children: "\u22EE\u22EE" })
      }
    );
    return /* @__PURE__ */ jsx(RichTooltip, { trigger: button, content: description });
  }
);
var DraggableListContext = createContext(null);
function useDraggableListContext() {
  return useContext(DraggableListContext);
}
function Announcer({ message }) {
  const [flip, setFlip] = useState(false);
  const last = useRef("");
  useEffect(() => {
    if (message === last.current) return;
    last.current = message;
    setFlip((f) => !f);
  }, [message]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "status",
      "aria-live": "polite",
      "aria-atomic": "true",
      className: "dnd__announcer",
      style: {
        position: "absolute",
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
        border: 0
      },
      children: [
        /* @__PURE__ */ jsx("span", { "data-flip": flip ? "a" : "", children: flip ? message : "" }),
        /* @__PURE__ */ jsx("span", { "data-flip": flip ? "" : "b", children: flip ? "" : message })
      ]
    }
  );
}
function DraggableList(props) {
  const validated = validateInput(DraggableListPropsSchema, "DraggableList", props);
  const { items, getId, renderItem, onReorder, ariaLabel, className, itemTypeFor } = validated;
  const [message, setMessage] = useState("");
  const state = useDraggableList({ items, getId, onReorder, itemTypeFor });
  const announce = useCallback(
    (text) => setMessage(text),
    []
  );
  const ctxValue = useMemo(
    () => ({
      grabbedIndex: state.grabbedIndex,
      grab(index) {
        state.grab(index);
        const id = items[index] ? getId(items[index]) : "";
        announce(`Item ${id} grabbed. Position ${index + 1} of ${items.length}.`);
      },
      dropAt(index) {
        const origin = state.originIndex;
        state.dropAt(index);
        if (origin !== null && origin !== index) {
          announce(`Dropped at position ${index + 1} of ${items.length}.`);
        } else {
          announce(`Released at original position ${index + 1}.`);
        }
      },
      moveBy(delta) {
        const cur = state.grabbedIndex;
        if (cur == null) return;
        const next = Math.max(0, Math.min(items.length - 1, cur + delta));
        if (next === cur) return;
        state.moveBy(delta);
        announce(`Moved to position ${next + 1} of ${items.length}.`);
      },
      moveIndex(from, to) {
        state.moveIndex(from, to);
        announce(`Reordered from position ${from + 1} to position ${to + 1}.`);
      },
      cancelToOrigin() {
        const origin = state.originIndex;
        const cur = state.grabbedIndex;
        if (origin != null && cur != null && origin !== cur) {
          state.moveIndex(cur, origin);
        }
        state.cancel();
        announce("Drag cancelled. Item returned to original position.");
      }
    }),
    [state, items, getId, announce]
  );
  const cls = ["dnd", className].filter(Boolean).join(" ");
  if (items.length === 0) {
    return /* @__PURE__ */ jsx(
      ErrorBoundary,
      {
        fallback: (err, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error: err, reset }),
        children: /* @__PURE__ */ jsx(EmptyState, { title: "Empty list", description: "No items to reorder." })
      }
    );
  }
  return /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallback: (err, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error: err, reset }),
      children: /* @__PURE__ */ jsxs(DraggableListContext.Provider, { value: ctxValue, children: [
        /* @__PURE__ */ jsx(
          "ol",
          {
            className: cls,
            role: "list",
            "aria-label": ariaLabel ?? "Reorderable list",
            ...withConformance(WEB_DND_LIB, LIB_VERSION),
            children: items.map((it, i) => /* @__PURE__ */ jsx(Fragment, { children: renderItem(it, i) }, getId(it)))
          }
        ),
        /* @__PURE__ */ jsx(Announcer, { message })
      ] })
    }
  );
}
var DEFAULT_TYPE = "application/x-norkron-dnd";
function useDrag(options) {
  const validated = validateInput(UseDragOptionsSchema, "useDrag", options);
  const { id, itemType, disabled, onDragStart, onDragEnd } = validated;
  const [isDragging, setDragging] = useState(false);
  const effectiveType = itemType ?? "default";
  const onStart = useCallback(
    (e) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(DEFAULT_TYPE, JSON.stringify({ id, itemType: effectiveType }));
      e.dataTransfer.setData("text/plain", id);
      setDragging(true);
      onDragStart?.(id);
    },
    [disabled, id, effectiveType, onDragStart]
  );
  const onEnd = useCallback(
    (e) => {
      setDragging(false);
      onDragEnd?.(id, e.dataTransfer.dropEffect === "move");
    },
    [id, onDragEnd]
  );
  return useMemo(
    () => ({
      isDragging,
      bind: {
        draggable: true,
        onDragStart: onStart,
        onDragEnd: onEnd,
        "data-dnd-id": id,
        "data-dnd-type": effectiveType,
        "data-dnd-state": isDragging ? "dragging" : "idle"
      }
    }),
    [isDragging, onStart, onEnd, id, effectiveType]
  );
}
function readDndPayload(e) {
  const raw = e.dataTransfer?.getData(DEFAULT_TYPE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === "string" && typeof parsed.itemType === "string") {
      return parsed;
    }
  } catch {
  }
  return null;
}
function useDrop(options = {}) {
  const validated = validateInput(UseDropOptionsSchema, "useDrop", options);
  const { accept, disabled, onDrop, onDragEnter, onDragLeave } = validated;
  const [isOver, setOver] = useState(false);
  const willAccept = useCallback(
    (payload) => {
      if (!payload) return false;
      if (!accept || accept.length === 0) return true;
      return accept.includes(payload.itemType);
    },
    [accept]
  );
  const handleOver = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },
    [disabled]
  );
  const handleDrop = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      setOver(false);
      const payload = readDndPayload(e);
      if (!willAccept(payload)) {
        warn({
          fn: "useDrop",
          code: "WEB_DND_DROP_REJECTED",
          message: "drop rejected \u2014 itemType not in accept list",
          received: payload?.itemType ?? null,
          expected: (accept ?? []).join(" | ") || "<any>"
        });
        return;
      }
      if (payload) onDrop?.(payload, e);
    },
    [disabled, willAccept, onDrop, accept]
  );
  const handleEnter = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      const payload = readDndPayload(e);
      if (willAccept(payload)) setOver(true);
      onDragEnter?.(payload);
    },
    [disabled, willAccept, onDragEnter]
  );
  const handleLeave = useCallback(
    (_e) => {
      if (disabled) return;
      setOver(false);
      onDragLeave?.();
    },
    [disabled, onDragLeave]
  );
  return useMemo(
    () => ({
      isOver,
      bind: {
        onDragOver: handleOver,
        onDrop: handleDrop,
        onDragEnter: handleEnter,
        onDragLeave: handleLeave,
        "data-dnd-target": true,
        "data-dnd-over": isOver
      }
    }),
    [isOver, handleOver, handleDrop, handleEnter, handleLeave]
  );
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-dnd";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-dnd"]')
    );
    if (instances.length === 0) return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
    for (const instance of instances) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "DND_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (instance.getAttribute("draggable") === "true") {
        const hasHandle = instance.querySelector("[data-keyboard-drag-handle]") !== null || instance.querySelector("[aria-keyshortcuts]") !== null;
        if (!hasHandle) {
          findings.push({
            libId: LIB_ID,
            code: "DND_NO_KEYBOARD_FALLBACK",
            category: "a11y",
            severity: "major",
            message: "Draggable element exposes no keyboard handle. Keyboard-only users cannot reorder.",
            selector: selectorFor(instance),
            suggestedFix: "Render the <KeyboardDragHandle> primitive inside each draggable item; it exposes \u2325\u2191/\u2325\u2193 keyboard shortcuts."
          });
        }
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "DND_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
var WEB_DND_LIB2 = "@norkron/web-dnd";
registerLazy(
  WEB_DND_LIB2,
  () => selfCheck
);
var DraggableItem = forwardRef(
  function DraggableItem2(props, ref) {
    const validated = validateInput(DraggableItemPropsSchema, "DraggableItem", props);
    const { id, index, children, className, itemType } = validated;
    const ctx = useDraggableListContext();
    const grabbed = ctx?.grabbedIndex === index;
    useFocusRestore(Boolean(grabbed));
    const drag = useDrag({ id, itemType });
    const drop = useDrop({
      accept: itemType ? [itemType] : void 0,
      onDrop: (payload) => {
        if (!ctx) return;
        const fromAttr = document.querySelector(`[data-dnd-id="${payload.id}"]`);
        const fromIdx = fromAttr ? Number(fromAttr.getAttribute("data-dnd-index")) : -1;
        if (fromIdx >= 0) ctx.moveIndex(fromIdx, index);
      }
    });
    const cls = [
      "dnd__item",
      grabbed ? "dnd__item--grabbed" : null,
      drag.isDragging ? "dnd__item--dragging" : null,
      drop.isOver ? "dnd__item--drop-over" : null,
      className
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsxs(
      "li",
      {
        ref,
        className: cls,
        "data-dnd-index": index,
        role: "listitem",
        "aria-roledescription": "draggable item",
        ...drag.bind,
        ...drop.bind,
        ...withConformance(WEB_DND_LIB2, LIB_VERSION),
        children: [
          drop.isOver ? /* @__PURE__ */ jsx("span", { className: "dnd__drop-indicator dnd__drop-indicator--active", "aria-hidden": "true" }) : null,
          children
        ]
      }
    );
  }
);
var selfCheckInputSchema = z.object({
  opts: SelfCheckOptionsSchema.optional()
}).strict();
var ConformanceSeveritySchema = z.enum(["critical", "major", "minor", "info"]);
var ConformanceCategorySchema = z.enum([
  "theme",
  "stylesheet",
  "a11y",
  "layout",
  "i18n",
  "cardinality",
  "identity",
  "schema",
  "flow",
  "deprecation",
  "perf"
]);
var SelfCheckFindingSchema = z.object({
  libId: z.string().min(1),
  code: z.string().min(1),
  category: ConformanceCategorySchema,
  severity: ConformanceSeveritySchema,
  message: z.string().min(1),
  selector: z.string().optional(),
  suggestedFix: z.string().optional(),
  extras: z.record(z.unknown()).optional()
}).strict();
var LIB_VERSIONSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?$/, "LIB_VERSION must be semver");
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-dnd",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded in dnd.css (no @media / @container, fluid flex layout)
    // + draggable-list.tsx / draggable-item.tsx / keyboard-drag-handle.tsx
    // (no viewport-keyed branching). Honest baseline: 'full' at every
    // tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

export { ConformanceCategorySchema, ConformanceSeveritySchema, DraggableItem, DraggableItemPropsSchema, DraggableList, DraggableListContext, DraggableListPropsSchema, KeyboardDragHandle, KeyboardDragHandlePropsSchema, LIB_VERSION, LIB_VERSIONSchema, RESPONSIVE_CONTRACT, SelfCheckFindingSchema, SelfCheckOptionsSchema, UseDragOptionsSchema, UseDraggableListOptionsSchema, UseDropOptionsSchema, readDndPayload, selfCheck, selfCheckInputSchema, useDrag, useDraggableList, useDraggableListContext, useDrop };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map